import { useEffect, useState } from "react";
import { HandHeart, HeartHandshake, Leaf, GraduationCap, Footprints, Sprout, TreePine, PawPrint, Globe, Droplets, Recycle, Ban, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import PageHero from "@/components/PageHero";

const loadRazorpay = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

const PRESET_AMOUNTS = [500, 1000, 2500, 5000];

const IMPACT_AREAS = [
  { icon: Leaf, t: "Clean & Green Destinations", d: "Post-event clean-up drives, no-plastic campaigns and destination care at every venue." },
  { icon: Footprints, t: "Running For All", d: "Free and supported participation for underprivileged youth, village runners and first-time athletes." },
  { icon: GraduationCap, t: "Community & Youth", d: "Sports kits, school fitness sessions and volunteer skill-building in host communities." },
  { icon: HeartHandshake, t: "Local Livelihoods", d: "Event income routed to homestays, local crews, guides and small businesses around each destination." },
];

const RUN_CAUSES = [
  { slug: "go-green", icons: [Sprout], t: "Go Green", d: "Green cover drives and eco-friendly race villages at every destination." },
  { slug: "save-forest-animals", icons: [TreePine, PawPrint], t: "Save Forest, Save Animals", d: "Runs that respect wildlife corridors and spread forest conservation awareness." },
  { slug: "save-environment", icons: [Globe], t: "Save Environment", d: "Low-waste events, carbon-conscious planning and clean destination pledges." },
  { slug: "save-water-earth", icons: [Droplets], t: "Save Water — Save Earth", d: "Water conservation themes, pond & river clean-up runs with host communities." },
  { slug: "say-no-plastic", icons: [Recycle], t: "Say No To Plastic", d: "Plastic-free bib expos, refill hydration stations and reusable race kits." },
  { slug: "say-no-drugs", icons: [Ban], t: "Say No To Drugs", d: "Youth fitness runs carrying the anti-drug message — sport over substance." },
  { slug: "beti-bachao-beti-padhao", icons: [GraduationCap], t: "Beti Bachao Beti Padhao", d: "Encouraging girls' participation in sport and education through race platforms." },
];

export default function GiveBack() {
  const [s, setS] = useState(null);
  const [cause, setCause] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", organization: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [donForm, setDonForm] = useState({ name: "", email: "", amount: "" });
  const [donBusy, setDonBusy] = useState(false);

  useEffect(() => {
    api.get("/settings/public").then((r) => setS(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enquiries", {
        ...form,
        type: cause ? `NGO / Social Organisation — ${cause}` : "NGO / Social Organisation",
      });
      toast.success("Thank you! Our team will reach out to your organisation soon.");
      setForm({ name: "", email: "", phone: "", organization: "", message: "" });
      setCause(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const donateUpi = s?.donation_upi || "";

  const donate = async () => {
    const amount = parseInt(donForm.amount, 10);
    if (!donForm.name.trim() || !donForm.email.trim()) { toast.error("Please enter your name and email for the receipt."); return; }
    if (!amount || amount < 10) { toast.error("Please choose or enter a donation amount (minimum Rs.10)."); return; }
    setDonBusy(true);
    try {
      const { data } = await api.post("/donations/order", { name: donForm.name.trim(), email: donForm.email.trim(), amount, cause: cause || "" });
      const loaded = await loadRazorpay();
      if (!loaded) throw new Error("payment_sdk");
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "SPORTIVE — Run For A Cause",
        description: cause ? `Donation — ${cause}` : "Donation",
        order_id: data.order_id,
        prefill: { name: donForm.name.trim(), email: donForm.email.trim() },
        theme: { color: "#9BAE1B" },
        modal: { ondismiss: () => toast.info("Payment cancelled — no amount was charged.") },
        handler: async (resp) => {
          try {
            await api.post("/donations/verify", { ...resp, don_id: data.don_id });
            toast.success(`Thank you, ${donForm.name.trim().split(" ")[0]}! Donation of Rs.${amount} confirmed. Receipt on its way to your email.`);
            setDonForm({ name: "", email: "", amount: "" });
          } catch {
            toast.error("Payment verification failed. Write to contact@sportive.co.in with Donation ID: " + data.don_id);
          }
        },
      });
      rzp.open();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start payment. Please try again.");
    } finally {
      setDonBusy(false);
    }
  };

  return (
    <div data-testid="giveback-page">
      <PageHero eyebrow="RUN FOR A CAUSE" title="Sport That Gives Back."
        subtitle="Every SPORTIVE event is built with its host community. Through our philanthropic initiative, runners, NGOs and well-wishers can turn race day into lasting local impact."
        image="https://images.unsplash.com/photo-1651678938439-fc3f8f03eda5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxydXJhbCUyMGluZGlhJTIwdmlsbGFnZSUyMGdyZWVuJTIwbGFuZHNjYXBlfGVufDB8fHx8MTc4OTA2ODIwN3ww&ixlib=rb-4.1.0&q=85" />

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <p className="eyebrow">RUN FOR A CAUSE</p>
        <h2 className="mt-3 font-display text-3xl font-bold uppercase tracking-tight text-white sm:text-4xl">Pick Your Cause. Run For It.</h2>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-400">
          Select a cause below — your donation or NGO proposal will be tagged to it. Every SPORTIVE edition carries one or more of these awareness themes on its banners, bibs and finish-line messages.
        </p>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {RUN_CAUSES.map((c) => {
            const active = cause === c.t;
            return (
              <button key={c.slug} type="button" data-testid={`cause-card-${c.slug}`}
                onClick={() => setCause(active ? null : c.t)}
                aria-pressed={active}
                className={`card-k group relative p-6 text-left transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 ${
                  active ? "border-brand-olive shadow-[0_0_24px_rgba(155,174,27,0.25)]" : "hover:border-brand-cyan/50"
                }`}>
                {active && (
                  <span data-testid={`cause-selected-${c.slug}`} className="absolute right-4 top-4 flex h-6 w-6 items-center justify-center rounded-full bg-brand-olive text-brand-ink">
                    <Check size={14} strokeWidth={3} />
                  </span>
                )}
                <span className="flex items-center gap-2">
                  {c.icons.map((Icon, i) => (
                    <Icon key={i} size={26} className={active ? "text-brand-olive" : "text-brand-cyan transition-colors group-hover:text-brand-olive"} />
                  ))}
                </span>
                <h3 className="mt-4 font-display text-xl font-bold uppercase tracking-wide text-white">{c.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{c.d}</p>
              </button>
            );
          })}
        </div>
        <p data-testid="cause-selection-status" className="mt-6 font-mono text-[11px] tracking-[0.2em] text-slate-500">
          {cause ? <>SELECTED CAUSE: <span className="text-brand-olive">{cause.toUpperCase()}</span></> : "NO CAUSE SELECTED — TAP A CARD ABOVE"}
        </p>
      </section>

      <section className="border-t border-brand-line mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <p className="eyebrow">WHAT YOUR SUPPORT POWERS</p>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {IMPACT_AREAS.map((c) => (
            <div key={c.t} className="card-k p-6 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-brand-olive/50">
              <c.icon size={24} className="text-brand-olive" />
              <h3 className="mt-4 font-display text-xl font-bold text-white">{c.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-brand-line bg-brand-surface/40">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="eyebrow">DONATE</p>
            <h2 className="mt-3 font-display text-4xl font-bold uppercase tracking-tight text-white">Every Rupee Reaches the Route.</h2>
            <p className="mt-4 leading-relaxed text-slate-300">
              Contributions support community participation, village sports kits, destination clean-up drives and free entries for young runners from host communities. Donations are collected for <span className="font-semibold text-brand-cyan">{s?.donation_beneficiary || "SPORTIVE — Real Sports India"}</span> and utilised against published event-community initiatives.
            </p>
            {cause && (
              <div className="card-k mt-6 inline-flex items-center gap-3 border-brand-olive/40 px-5 py-3">
                <span className="font-mono text-[10px] tracking-[0.2em] text-slate-500">SUPPORTING</span>
                <span data-testid="donate-cause-chip" className="font-display text-sm font-bold uppercase tracking-wide text-brand-olive">{cause}</span>
              </div>
            )}
            {donateUpi && (
              <div className="card-k mt-6 inline-flex items-center gap-3 px-5 py-3">
                <span className="font-mono text-[10px] tracking-[0.2em] text-slate-500">UPI</span>
                <span data-testid="donate-upi" className="font-mono text-lg font-semibold text-brand-olive">{donateUpi}</span>
              </div>
            )}
            <p className="mt-4 text-xs text-slate-500">For receipts or large contributions, write to us — we respond within one working day.</p>
          </div>
          <div className="card-k border-brand-olive/30 p-8">
            <div className="text-center">
              <HandHeart size={36} className="mx-auto text-brand-olive" />
              <h3 className="mt-4 font-display text-2xl font-bold text-white">Make a Contribution</h3>
              <p className="mt-2 text-sm text-slate-400">Any amount helps put one more village runner on the start line.</p>
            </div>
            <div className="mt-5 grid grid-cols-4 gap-2">
              {PRESET_AMOUNTS.map((a) => (
                <button key={a} type="button" data-testid={`donate-amount-${a}`} onClick={() => setDonForm({ ...donForm, amount: String(a) })}
                  className={`rounded-lg border px-2 py-2 font-mono text-xs font-semibold transition-colors ${donForm.amount === String(a) ? "border-brand-olive bg-brand-olive/15 text-brand-olive" : "border-brand-line text-slate-400 hover:border-brand-cyan/50"}`}>₹{a}</button>
              ))}
            </div>
            <input data-testid="donate-amount-custom" type="number" min="10" placeholder="Or enter your amount (₹)" className="input-k mt-3" value={donForm.amount} onChange={(e) => setDonForm({ ...donForm, amount: e.target.value })} />
            <div className="mt-3 space-y-3">
              <input data-testid="donate-name" placeholder="Your name" className="input-k" value={donForm.name} onChange={(e) => setDonForm({ ...donForm, name: e.target.value })} />
              <input data-testid="donate-email" type="email" placeholder="Email (for receipt)" className="input-k" value={donForm.email} onChange={(e) => setDonForm({ ...donForm, email: e.target.value })} />
            </div>
            <button data-testid="donate-button" onClick={donate} disabled={donBusy} className="btn-kinetic btn-primary-k mt-4 w-full justify-center disabled:opacity-60">{donBusy ? "STARTING SECURE PAYMENT..." : "DONATE SECURELY"}</button>
            <p className="mt-3 text-center font-mono text-[10px] tracking-[0.15em] text-slate-600">SECURE UPI / CARDS / NETBANKING VIA RAZORPAY</p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-4 py-20 sm:px-6">
        <p className="eyebrow">FOR NGOs & SOCIAL ORGANISATIONS</p>
        <h2 className="mt-3 font-display text-3xl font-bold uppercase tracking-tight text-white">Partner Your Cause With a Race.</h2>
        <p className="mt-3 text-sm text-slate-400">Awareness runs, charity bibs, fundraiser categories and community programs — if your organisation works in health, environment, education or rural development, let's design something together.</p>
        <form onSubmit={submit} className="card-k mt-8 space-y-4 p-7">
          {cause && (
            <div data-testid="ngo-cause-chip" className="flex items-center justify-between rounded-lg border border-brand-olive/40 bg-brand-olive/10 px-4 py-2.5">
              <span className="font-mono text-[10px] tracking-[0.2em] text-slate-400">LINKED CAUSE</span>
              <span className="font-display text-sm font-bold uppercase tracking-wide text-brand-olive">{cause}</span>
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <input data-testid="ngo-name" required placeholder="Contact person" className="input-k" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input data-testid="ngo-org" required placeholder="Organisation / NGO name" className="input-k" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <input data-testid="ngo-email" required type="email" placeholder="Email" className="input-k" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input data-testid="ngo-phone" placeholder="Phone" className="input-k" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <textarea data-testid="ngo-message" required rows={4} placeholder="Tell us about your cause and how you'd like to collaborate..." className="input-k" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
          <button data-testid="ngo-submit" disabled={busy} className="btn-kinetic btn-primary-k w-full justify-center disabled:opacity-60">{busy ? "Sending..." : "SUBMIT PROPOSAL"}</button>
        </form>
      </section>
    </div>
  );
}
